// Design tokens for the advanced-scheduler planning surfaces
// (Work Plan, Roster, Labor Standards).
//
// SOURCE OF TRUTH: the Wilya Design System (see AGENTS.md). Values below are
// the Wilya tokens, not invented ones. Key rules encoded here:
//   1. Hairlines (1.5px solid ink), not shadows — that is what makes Wilya
//      look engineered. Soft shadows are a last resort.
//   2. Small radii. 4px is the default; nothing rounder than 16px.
//   3. No gradients. Flat fills only.
//   4. Brick is scarce — accent, CTAs, key numbers, caps eyebrows. Never body text.
//   5. Two type roles: Trebuchet for headings, Inter for product UI.
//   8. Mono for machine data (IDs, VINs, shift codes, times).
// Support colors (sulfur/weld/steel/mill) are for data viz + status ONLY,
// never decoration.

export const planner = {
	// ---- Foundational color (4 tokens do most of the work) ----
	brick: "#C85E51",
	brickSoft: "#F1C5BE",
	brickDeep: "#A84838",
	navy: "#1A2442",
	alloy: "#F2F2F2",
	alloyWarm: "#F7F3EF",
	paper: "#FFFFFF",

	// ---- Canvas & surfaces (names kept stable for existing components) ----
	canvas: "#F7F3EF",        // alloy-warm: page background
	surface: "#FFFFFF",       // paper: cards, panels
	surfaceSubtle: "#F2F2F2", // alloy: table headers, lane fills
	hairline: "#E4E6EC",      // dividers inside a panel
	hairlineStrong: "#D1D3D9",// navy-20: panel edges, hover borders

	// ---- Ink ----
	ink: "#121826",
	inkSecondary: "#5B6380",  // ink-60: muted text
	inkMuted: "#767C8E",      // navy-60: hints

	// ---- Accent (brick — scarce by rule) ----
	accent: "#C85E51",
	accentSoft: "#F1C5BE",
	accentBorder: "#E3B0A7",
	accentDeep: "#A84838",    // pressed state

	// ---- Support ramp: data viz + status only ----
	sulfur: "#E7B73C",
	weld: "#3AA9E0",
	steel: "#4B5B7A",
	mill: "#8A6F5C",

	// ---- Staffing / status semantics (Wilya status colors) ----
	staffed: "#2E7D5B",       // success
	staffedSoft: "#E6EFEB",
	partial: "#C9851F",       // warning
	partialSoft: "#F9F0E4",
	unstaffed: "#B3342A",     // danger
	unstaffedSoft: "#F6E7E5",
	unplanned: "#5B6380",     // draft: alloy fill + hairline border
	unplannedSoft: "#F2F2F2",

	// ---- Shape & elevation ----
	// Radii are small on purpose. Borders carry structure; shadows are rare.
	radius: 6,                // r-md — cards, panels
	radiusSm: 4,              // r-sm — controls, chips
	radiusXs: 2,              // r-xs — status badges
	borderWidth: 1.5,         // the Wilya hairline weight
	shadowRest: "none",       // rule 1: the border does the work
	shadowHover: "0 2px 6px rgba(26, 36, 66, 0.08)",
	shadowDialog: "0 12px 32px rgba(26, 36, 66, 0.16)",
	shadowFold: "6px 6px 0 #C85E51", // signature — ONE per surface, max

	// ---- Type ----
	fontBrand: "'Trebuchet MS', 'Segoe UI', Tahoma, sans-serif",
	fontUi: "'Inter', 'Helvetica Neue', Arial, sans-serif",
	fontMono: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",

	// Caps labels are wide, and brick on light grounds (rule 7).
	labelCaps: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.14em",
		textTransform: "uppercase" as const,
	},

	// The complete type scale for planner surfaces. Headings use the brand
	// face (never below 14px); everything else in the product is Inter.
	// Icons are sized independently.
	type: {
		pageTitle: {
			fontFamily: "'Trebuchet MS', 'Segoe UI', Tahoma, sans-serif",
			fontSize: 20,
			fontWeight: 700,
			lineHeight: 1.05,
			letterSpacing: "-0.02em",
		},
		title: { fontSize: 15, fontWeight: 600, lineHeight: 1.35, letterSpacing: "-0.01em" },
		stat: { fontSize: 18, fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" },
		body: { fontSize: 13, fontWeight: 400, lineHeight: 1.5 },
		bodyStrong: { fontSize: 13, fontWeight: 600, lineHeight: 1.5 },
		meta: { fontSize: 12, fontWeight: 400, lineHeight: 1.45 },
		metaStrong: { fontSize: 12, fontWeight: 600, lineHeight: 1.45 },
	},
} as const;

export type StaffingState = "staffed" | "partial" | "unstaffed" | "unplanned";

export const staffingColor = (state: StaffingState) =>
	({
		staffed: planner.staffed,
		partial: planner.partial,
		unstaffed: planner.unstaffed,
		unplanned: planner.unplanned,
	}[state]);

export const staffingSoft = (state: StaffingState) =>
	({
		staffed: planner.staffedSoft,
		partial: planner.partialSoft,
		unstaffed: planner.unstaffedSoft,
		unplanned: planner.unplannedSoft,
	}[state]);

export const staffingLabel = (state: StaffingState) =>
	({
		staffed: "Fully staffed",
		partial: "Partially staffed",
		unstaffed: "Not staffed",
		unplanned: "No labor standard",
	}[state]);

// ---- Categorical colors for job roles / phases (data viz only) ----
// One ramp for the whole product, drawn from the Wilya support colors so a
// role reads the same on the Work Plan, the Roster and a labor standard.
// Deliberately NOT the MUI or Tableau palettes those surfaces used before.
export const ROLE_COLORS: readonly string[] = [
	planner.steel,   // #4B5B7A
	planner.weld,    // #3AA9E0
	planner.sulfur,  // #E7B73C
	planner.mill,    // #8A6F5C
	planner.navy,    // #1A2442
	planner.accent,  // #C85E51 — used last; brick stays scarce
];

// Stable colour for a role name: same input always yields the same ramp slot.
export const roleColor = (roleName?: string | null): string => {
	if (!roleName) return planner.steel;
	let hash = 0;
	for (let i = 0; i < roleName.length; i += 1) {
		hash = (hash * 31 + roleName.charCodeAt(i)) % 100000;
	}
	return ROLE_COLORS[hash % ROLE_COLORS.length];
};

// Text that sits on a filled role swatch. Sulfur is the one light fill.
export const roleTextColor = (fill: string): string =>
	fill === planner.sulfur ? planner.navy : planner.paper;
